// Pure business logic for the fuel plan generation flow.
//
// Nothing here touches React or the network. These are the "private methods" of
// the generate-fuel-plan handler (see use-submit-fuel-plan.ts), pulled out as
// plain functions so they can be unit tested in one line and reused by any form
// that builds a fuel plan. Data access lives in api.ts; orchestration lives in
// the hook.

import { v4 as uuidv4 } from "uuid";
import type { FuelLocation, FuelPlanProParams, FuelPlanRoute } from "./api";
import type { Tractor } from "features/app/tractors/api";
import type { SendEmailPayload } from "./messaging";

// ─── Form shape ───
// The react-hook-form values the fuel plan forms submit.

export interface FuelPlanFormLocation {
  description: string;
}

export interface FuelPlanFormData {
  tractor: Tractor;
  tractorFuel: number;
  originLocation: FuelPlanFormLocation;
  destinationLocation: FuelPlanFormLocation;
  waypoints: { waypoint: FuelPlanFormLocation }[];
  routeMode: "short" | "fast";
}

// ─── Normalized output ───
// A route as the UI consumes it: fuel stops carry a client id and active flag,
// and the selected tractor is attached with its resolved fuel level.

export interface NormalizedFuelLocation extends FuelLocation {
  loc_id: string;
  active?: boolean;
}

export interface NormalizedFuelPlanRoute extends Omit<FuelPlanRoute, "fuelPurchaseLocations"> {
  fuelPurchaseLocations: NormalizedFuelLocation[];
  tractor: Tractor & { fuelLevelInGallons: number };
}

/**
 * The generated fuel plan held in fuel-optimizer state: one normalized route
 * per ranking from the Pro optimizer. `null` before a plan is generated.
 */
export type FuelPlan = NormalizedFuelPlanRoute[];

export function resolveTractorFuelLevelInGallons({
  liveFuelLevelInPercent,
  tankCapacityInGallons,
  reportedFuelLevelInGallons,
  customerDefaultInGallons,
}: {
  liveFuelLevelInPercent?: number | null;
  tankCapacityInGallons?: number;
  reportedFuelLevelInGallons?: number;
  customerDefaultInGallons: number;
}): number {
  if (
    typeof liveFuelLevelInPercent === "number" &&
    Number.isFinite(liveFuelLevelInPercent) &&
    liveFuelLevelInPercent >= 0 &&
    liveFuelLevelInPercent <= 100 &&
    typeof tankCapacityInGallons === "number" &&
    Number.isFinite(tankCapacityInGallons) &&
    tankCapacityInGallons > 0
  ) {
    return Math.round((liveFuelLevelInPercent / 100) * tankCapacityInGallons);
  }

  return typeof reportedFuelLevelInGallons === "number" && Number.isFinite(reportedFuelLevelInGallons)
    ? reportedFuelLevelInGallons
    : customerDefaultInGallons;
}

// ─── Request builders ───

function viaFrom(formData: FuelPlanFormData): string[] {
  return formData.waypoints.map((w) => w.waypoint.description);
}

export function buildFuelPlanProParams(formData: FuelPlanFormData, customer: string): FuelPlanProParams {
  return {
    customer,
    origin: formData.originLocation.description,
    destination: formData.destinationLocation.description,
    via: viaFrom(formData),
    tractorFuel: formData.tractorFuel,
    tractorFuelCapacity: formData.tractor.tank_capacity,
    MPG: formData.tractor.MPG,
    routeMode: formData.routeMode,
  };
}

// ─── Response normalization ───

// Keep only the fuel stops the optimizer chose to include, tagging each with a
// stable client id. `markActive` mirrors the single/pro flows, which flag stops
// active so the map can toggle them.
export function withLocationIds(locations: FuelLocation[], markActive = false): NormalizedFuelLocation[] {
  return locations.reduce<NormalizedFuelLocation[]>((filtered, fpl) => {
    if (fpl.include) {
      filtered.push(markActive ? { loc_id: uuidv4(), active: true, ...fpl } : { loc_id: uuidv4(), ...fpl });
    }
    return filtered;
  }, []);
}

function attachTractor(formData: FuelPlanFormData): NormalizedFuelPlanRoute["tractor"] {
  return {
    ...formData.tractor,
    fuelLevelInGallons: Number(formData.tractorFuel),
  };
}

export function normalizeProRoutes(routes: FuelPlanRoute[], formData: FuelPlanFormData): NormalizedFuelPlanRoute[] {
  return routes
    .map((route) => ({
      ...route,
      fuelPurchaseLocations: withLocationIds(route.fuelPurchaseLocations, true),
      tractor: attachTractor(formData),
    }))
    .sort((a, b) => a.optimalRanking - b.optimalRanking);
}

// ─── Ops fallback ───

// When generation runs long, ops gets emailed the request so they can follow up.
export function buildTimeoutEmailPayload(
  formData: FuelPlanFormData,
  customer: string
): SendEmailPayload {
  return {
    to: ["sales@ifuelsmart.com"],
    subject: "Fuel Solution Pro Generation: Exceeded 10 seconds",
    html: JSON.stringify({
      customer,
      origin: formData.originLocation.description,
      destination: formData.destinationLocation.description,
      via: viaFrom(formData),
      tractorFuel: formData.tractorFuel,
      tractorFuelCapacity: formData.tractor.tank_capacity,
      MPG: formData.tractor.MPG,
    }),
  };
}
